import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useCompanyHours from './useCompanyHours';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import collect, { Collection } from 'collect.js';
import { TFunction } from 'i18next';
import { useLocationData } from '../../modals/order-method/provider/LocationDataProvider';
import { companyHasOnSite, includesOrderMethod } from '../../utils/on-site';

export default function useOrderMethodsStatus(data?: CompanyDetailsInterface) {
  const { company: reduxCompany } = useSelector((state: StoreInterface) => state.initialData);
  const company = data ?? reduxCompany;

  const companyHours = useCompanyHours({ data: company });
  const { orderMethod } = useLocationData();

  const pickupEnabled = useMemo(
    () => company?.has_live_orders && company?.has_pickup,
    [company?.has_live_orders, company?.has_pickup],
  );

  const deliveryEnabled = useMemo(
    () => company?.has_live_orders && company?.has_delivery && company?.delivery_enabled,
    [company?.has_live_orders, company?.has_delivery, company?.delivery_enabled],
  );

  const onSiteEnabled = !!company?.has_live_orders && companyHasOnSite(company);

  const takeoutEnabled = pickupEnabled || deliveryEnabled || onSiteEnabled;

  const pickupCurrentlyOpen = useMemo(
    () => pickupEnabled && companyHours?.open && !companyHours?.pickupClosed && !companyHours?.ordersArePaused,
    [pickupEnabled, companyHours],
  );

  const deliveryCurrentlyOpen = useMemo(
    () => deliveryEnabled && companyHours?.open && !companyHours?.deliveryClosed && !companyHours?.ordersArePaused,
    [deliveryEnabled, companyHours],
  );

  // Table ordering follows pickup's hours.
  const onSiteCurrentlyOpen = useMemo(
    () => onSiteEnabled && companyHours?.open && !companyHours?.pickupClosed && !companyHours?.ordersArePaused,
    [onSiteEnabled, companyHours],
  );

  const takeoutCurrentlyOpen = pickupCurrentlyOpen || deliveryCurrentlyOpen || onSiteCurrentlyOpen;

  const takeoutOpenForFutureOrders = takeoutEnabled && company?.has_scheduled_orders && company?.has_pre_order;

  const enabledOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect({
        [ORDER_METHODS.ON_SITE]: onSiteEnabled,
        [ORDER_METHODS.PICKUP]: pickupEnabled,
        [ORDER_METHODS.DELIVERY]: deliveryEnabled,
      })
        .reduce(
          // @ts-expect-error wrong package ts
          (carry: Collection<ORDER_METHODS>, enabled: boolean, method: ORDER_METHODS) =>
            carry.when(enabled, (result) => result.push(method)),
          collect(),
        )
        .toArray(),
    [onSiteEnabled, pickupEnabled, deliveryEnabled],
  );

  // The methods a guest can pick in the toggle. Table ordering is never one of them: only a table's
  // QR code starts it (PROD-724).
  const visibleOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect([ORDER_METHODS.PICKUP, ORDER_METHODS.DELIVERY])
        .reject((item) => includesOrderMethod(company?.hidden_order_methods, item))
        .toArray(),
    [company?.hidden_order_methods],
  );

  const openOrderMethods = useMemo<ORDER_METHODS[]>(
    () =>
      collect({
        [ORDER_METHODS.ON_SITE]: onSiteCurrentlyOpen,
        [ORDER_METHODS.PICKUP]: pickupCurrentlyOpen,
        [ORDER_METHODS.DELIVERY]: deliveryCurrentlyOpen,
      })
        .reduce(
          // @ts-expect-error wrong package ts
          (carry: Collection<ORDER_METHODS>, enabled: boolean, method: ORDER_METHODS) =>
            carry.when(enabled, (result) => result.push(method)),
          collect(),
        )
        .toArray(),
    [onSiteCurrentlyOpen, pickupCurrentlyOpen, deliveryCurrentlyOpen],
  );

  const isOrderMethodEnabled = useCallback(
    (method: ORDER_METHODS) => (enabledOrderMethods ?? []).includes(method),
    [enabledOrderMethods],
  );

  const isOrderMethodCurrentlyOpen = useCallback(
    (method: ORDER_METHODS) => (openOrderMethods ?? []).includes(method),
    [openOrderMethods],
  );

  const isOrderMethodOpenForFutureOrders = useCallback(
    (method: ORDER_METHODS) => {
      return isOrderMethodEnabled(method) && company?.has_scheduled_orders && company?.has_pre_order;
    },
    [
      openOrderMethods,
      isOrderMethodEnabled,
      company?.delivery_mode,
      company?.has_scheduled_orders,
      company?.has_pre_order,
    ],
  );

  const renderOrderMethodBasedCopy = useCallback(
    (method: ORDER_METHODS, t: TFunction, prefix: string, params: object = undefined): string =>
      t(`${prefix}.${method}`, params),
    [],
  );

  const hasAsapOrders = useCallback(
    (method?: ORDER_METHODS) => company?.has_asap_orders && isOrderMethodCurrentlyOpen(method ?? orderMethod),
    [company, isOrderMethodCurrentlyOpen, orderMethod],
  );

  const hasScheduledOrders = useCallback(
    (method?: ORDER_METHODS) =>
      company?.has_scheduled_orders && isOrderMethodOpenForFutureOrders(method ?? orderMethod),
    [company, isOrderMethodOpenForFutureOrders, orderMethod],
  );

  const orderingDisabled = useCallback(
    (method?: ORDER_METHODS) => !hasAsapOrders(method) && !hasScheduledOrders(method),
    [company],
  );

  return {
    takeoutEnabled,
    pickupEnabled,
    deliveryEnabled,
    onSiteEnabled,
    takeoutCurrentlyOpen,
    pickupCurrentlyOpen,
    deliveryCurrentlyOpen,
    onSiteCurrentlyOpen,
    takeoutOpenForFutureOrders,
    enabledOrderMethods,
    visibleOrderMethods,
    openOrderMethods,
    orderingDisabled,
    hasAsapOrders,
    hasScheduledOrders,
    isOrderMethodEnabled,
    isOrderMethodCurrentlyOpen,
    isOrderMethodOpenForFutureOrders,
    renderOrderMethodBasedCopy,
  };
}
